import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryError } from 'src/queries/network.utils'
import {
  ReceiptAgentCaseDecisionResponse,
  ReceiptAgentCaseResponse,
  ReceiptAgentRun,
  StartReceiptAgentRunParams,
  SubmitReceiptAgentCaseDecisionParams,
} from 'src/queries/organizations/receiptAgent/receiptAgent.models'
import {
  getReceiptAgent,
  getReceiptAgentCase,
  getReceiptAgentRuns,
  startReceiptAgentRun,
  submitReceiptAgentCaseDecision,
} from 'src/queries/organizations/receiptAgent/receiptAgent.network'
import { transactionsQueryKeys } from 'src/queries/queries.constants'

export const RECEIPT_AGENT_RUN_POLL_INTERVAL_MS = 3_000
export const RECEIPT_AGENT_RUN_IN_PROGRESS_ERROR_CODE =
  'RECEIPT_AGENT_RUN_IN_PROGRESS'

export const isReceiptAgentRunActive = (run: ReceiptAgentRun): boolean =>
  run.lifecycleStatus === 'PENDING' || run.lifecycleStatus === 'RUNNING'

export const receiptAgentQueryKeys = {
  status: (organizationId: string | undefined) =>
    ['receipt-agent', 'status', organizationId] as const,
  runs: (
    organizationId: string | undefined,
    accountingPeriodId: string | undefined,
  ) => ['receipt-agent', 'runs', organizationId, accountingPeriodId] as const,
  case: (organizationId: string | undefined, caseId: string | undefined) =>
    ['receipt-agent', 'case', organizationId, caseId] as const,
}

export const receiptAgentQueries = {
  status: (organizationId: string | undefined) =>
    queryOptions({
      queryKey: receiptAgentQueryKeys.status(organizationId),
      queryFn: () => getReceiptAgent(organizationId!),
      enabled: Boolean(organizationId),
      staleTime: 5 * 60 * 1000,
    }),
  runs: (
    organizationId: string | undefined,
    accountingPeriodId: string | undefined,
  ) =>
    queryOptions({
      queryKey: receiptAgentQueryKeys.runs(organizationId, accountingPeriodId),
      queryFn: () => getReceiptAgentRuns(organizationId!, accountingPeriodId!),
      enabled: Boolean(organizationId && accountingPeriodId),
      staleTime: 0,
      retry: 1,
      refetchInterval: ({ state }) =>
        state.data?.some(isReceiptAgentRunActive)
          ? RECEIPT_AGENT_RUN_POLL_INTERVAL_MS
          : false,
    }),
  case: (organizationId: string | undefined, caseId: string | undefined) =>
    queryOptions<ReceiptAgentCaseResponse>({
      queryKey: receiptAgentQueryKeys.case(organizationId, caseId),
      queryFn: () => {
        if (!organizationId || !caseId) {
          throw new Error('organizationId and caseId are required')
        }

        return getReceiptAgentCase(organizationId, caseId)
      },
      enabled: Boolean(organizationId && caseId),
    }),
}

export const receiptAgentMutations = {
  startRun: () =>
    mutationOptions<ReceiptAgentRun, QueryError, StartReceiptAgentRunParams>({
      mutationFn: ({ organizationId, accountingPeriodId }) =>
        startReceiptAgentRun(organizationId, accountingPeriodId),
    }),
  decide: () =>
    mutationOptions<
      ReceiptAgentCaseDecisionResponse,
      QueryError,
      SubmitReceiptAgentCaseDecisionParams
    >({
      mutationFn: (params) => submitReceiptAgentCaseDecision(params),
    }),
}

/** Refreshes the live flyout after a decision or an optimistic-lock conflict. */
export function useReceiptAgentCaseDecision(transactionId: string | undefined) {
  const queryClient = useQueryClient()
  const refreshTransactionDetailAndTables = ({
    organizationId,
    caseId,
  }: SubmitReceiptAgentCaseDecisionParams) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: transactionsQueryKeys.detail(transactionId),
        exact: true,
      }),
      queryClient.invalidateQueries({
        queryKey: transactionsQueryKeys.lists,
      }),
      queryClient.invalidateQueries({
        queryKey: receiptAgentQueryKeys.case(organizationId, caseId),
      }),
    ])

  return useMutation({
    ...receiptAgentMutations.decide(),
    onSuccess: (_data, variables) =>
      refreshTransactionDetailAndTables(variables),
    onError: (error, variables) => {
      if (error.status === 409)
        return refreshTransactionDetailAndTables(variables)
      return undefined
    },
  })
}
